import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from 'glamor';
import { Message, Icon, Loader } from 'semantic-ui-react';
import { FaCheckCircle, FaClock } from 'react-icons/fa';
import { spacing } from '../library/theme';
import Button from '../library/Button';
import Modal from '../library/Modal';
import CardBrandIcon from './CardBrandIcon';
import ChangeCardFormContainer from './ChangeCardFormContainer';
import formatDate from '../../utils/formatDate';

// Figma tokens (node 305-3212)
const figma = {
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  borderDefault: '#E2E8F0',
  borderSelected: '#0369A1',
  bgSelected: '#F0F9FF',
  badgeBg: '#E0F2FE',
  badgeText: '#0369A1',
  buttonBg: '#0F172A',
  dashBorder: '#CBD5E1',
};

const modalClass = css({
  borderRadius: 14,
  '&.ui.tiny.modal': {
    maxWidth: '480px',
  },
});

const headerWrapper = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const headerLeft = css({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
});

const headerTitle = css({
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '1.5em',
  margin: 0,
  color: figma.textPrimary,
});

const closeButton = css({
  cursor: 'pointer',
  color: figma.textSecondary,
  fontSize: 14,
  ':hover': { color: figma.textPrimary },
});

const backButton = css({
  cursor: 'pointer',
  color: figma.textSecondary,
  fontSize: 14,
  ':hover': { color: figma.textPrimary },
});

const cardList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const savedCardItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  border: `0.6px solid ${figma.borderDefault}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.15s',
  ':hover': {
    borderColor: figma.borderSelected,
  },
});

const savedCardItemSelected = css({
  borderColor: figma.borderSelected,
  backgroundColor: figma.bgSelected,
});

const savedCardLabel = css({
  flex: 1,
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '1.5em',
  color: figma.textPrimary,
});

const defaultBadge = css({
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 9,
  fontWeight: 500,
  lineHeight: '1.5em',
  color: figma.badgeText,
  backgroundColor: figma.badgeBg,
  borderRadius: 3,
  padding: '1px 5px',
});

const addMethodLink = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  height: 36,
  border: `0.6px dashed ${figma.dashBorder}`,
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  color: figma.textSecondary,
  backgroundColor: 'transparent',
  width: '100%',
  transition: 'all 0.15s',
  marginTop: 0,
  ':hover': {
    borderColor: figma.borderSelected,
    color: figma.borderSelected,
  },
});

const confirmButton = {
  margin: 0,
  marginTop: 12,
  backgroundColor: figma.buttonBg,
  color: '#FFFFFF',
  borderRadius: 8,
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 600,
  fontSize: 13,
  lineHeight: '1.5em',
  height: 40,
  padding: 0,
};

const errorMessageStyle = { marginBottom: 12 };

function SavedCardItem({ card, isSelected, onSelect }) {
  const handleClick = useCallback(() => onSelect(card.id), [card.id, onSelect]);

  return (
    <div
      className={css(savedCardItem, isSelected && savedCardItemSelected)}
      onClick={handleClick}
    >
      <CardBrandIcon brand={card.brand} size={16} />
      <span className={savedCardLabel}>
        {card.brand || 'Cartão'} ****{card.number}
      </span>
      {card.default && (
        <span className={defaultBadge}>Principal</span>
      )}
    </div>
  );
}

const successWrapper = css({
  textAlign: 'center',
  padding: '24px 0',
});

const successTitleRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginBottom: 8,
});

const successTitle = css({
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 18,
  fontWeight: 600,
  color: figma.textPrimary,
  margin: 0,
});

const successDescription = css({
  fontFamily: 'Montserrat, sans-serif',
  color: figma.textSecondary,
  fontSize: 14,
  margin: 0,
  lineHeight: '1.6em',
});

const processingWrapper = css({
  textAlign: 'center',
  padding: '32px 0 24px',
});

// Dentro do dimmer do modal o Semantic pinta o loader de branco (invisível
// no fundo branco); aqui volta às cores do Figma.
const processingLoader = css({
  '.ui.dimmer &.ui.loader:before': { borderColor: figma.borderDefault },
  '.ui.dimmer &.ui.loader:after': { borderColor: `${figma.borderSelected} transparent transparent` },
});

const processingTitle = css({
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 16,
  fontWeight: 600,
  color: figma.textPrimary,
  margin: '20px 0 8px',
});

// Renovação com cobrança imediata que o banco ainda está analisando. O modal
// acompanha o resultado (ver `waitForFirstCharge` no container).
function ProcessingScreen() {
  return (
    <div className={processingWrapper}>
      <Loader active inline="centered" size="medium" className={processingLoader} />
      <h3 className={processingTitle}>Processando pagamento…</h3>
      <p className={successDescription}>
        Estamos confirmando a cobrança com o seu banco.
        <br />
        Isso costuma levar só alguns segundos.
      </p>
    </div>
  );
}

// O banco não respondeu dentro do tempo de espera do modal.
function UnderReviewScreen({ onClose }) {
  return (
    <div className={successWrapper}>
      <div className={successTitleRow}>
        <FaClock size={20} color="#F59E0B" />
        <h3 className={successTitle}>Pagamento em análise</h3>
      </div>
      <p className={successDescription}>
        Seu banco ainda está analisando a cobrança. Você pode fechar esta janela:
        quando ela for aprovada, a assinatura aparece como <strong>Pago</strong> em Financeiro.
        <br />
        Se for recusada, a renovação é desfeita e você pode tentar com outro cartão.
      </p>
      <Button onClick={onClose} fluid style={{ ...confirmButton, marginTop: 40 }}>
        Fechar
      </Button>
    </div>
  );
}

export default function ResumeSubscriptionModal({
  open,
  onClose,
  creditCardTokens = [],
  onConfirm,
  onNewCardSubmit,
  loading,
  deferred,
  processing,
  underReview,
  showSetAsDefault,
}) {
  const [screen, setScreen] = useState('list');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const formResetRef = useRef(null);

  const defaultCardId = useMemo(() => {
    const defaultCard = creditCardTokens.find((c) => c.default);
    return defaultCard?.id || creditCardTokens[0]?.id || null;
  }, [creditCardTokens]);

  const effectiveSelectedId = selectedCardId !== null ? selectedCardId : defaultCardId;
  const handleShowNewCardForm = useCallback(() => setScreen('newCard'), []);

  useEffect(() => {
    if (selectedCardId === null && defaultCardId !== null) {
      setSelectedCardId(defaultCardId);
    }
  }, [defaultCardId, selectedCardId]);

  const handleClose = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setScreen('list');
    setSelectedCardId(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setScreen('list');
    setSubmitError(null);
  }, []);

  const handleConfirmSelection = useCallback(() => {
    if (!effectiveSelectedId) return;
    const card = creditCardTokens.find((c) => c.id === effectiveSelectedId);
    if (!card) return;

    setSubmitError(null);
    onConfirm(card.cardId)
      .catch((error) => {
        const msg = error?.response?.body?.errors?.[0]
          || error?.response?.body?.error
          || 'Ocorreu um erro ao renovar a assinatura. Tente novamente mais tarde.';
        setSubmitError(msg);
      });
  }, [effectiveSelectedId, creditCardTokens, onConfirm]);

  const handleNewCardSubmit = useCallback((cardData) => {
    setSubmitError(null);
    return onNewCardSubmit(cardData)
      .then(() => {
        if (formResetRef.current) formResetRef.current();
        setScreen('list');
        setSelectedCardId(null);
      })
      .catch((error) => {
        const msg = error?.response?.body?.errors?.[0]
          || error?.response?.body?.error
          || 'Ocorreu um erro ao processar o cartão. Tente novamente mais tarde.';
        setSubmitError(msg);
      });
  }, [onNewCardSubmit]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} className={modalClass} size="tiny">
      {!deferred && !processing && !underReview && (
        <Modal.Header>
          <div className={headerWrapper}>
            <div className={headerLeft}>
              {screen === 'newCard' && (
                <Icon name="arrow left" className={backButton} onClick={handleBack} />
              )}
              <h2 className={headerTitle}>
                {screen === 'list' ? 'Escolher forma de pagamento' : 'Novo método de pagamento'}
              </h2>
            </div>
            <Icon name="close" className={closeButton} onClick={handleClose} />
          </div>
        </Modal.Header>
      )}

      <Modal.Content>
        {submitError && !processing && (
          <Message error visible content={submitError} style={errorMessageStyle} />
        )}

        {processing ? (
          <ProcessingScreen />
        ) : underReview ? (
          <UnderReviewScreen onClose={handleClose} />
        ) : deferred ? (
          <div className={successWrapper}>
            <div className={successTitleRow}>
              <FaCheckCircle size={22} color="#22C55E" />
              <h3 className={successTitle}>Assinatura renovada com sucesso!</h3>
            </div>
            <p className={successDescription}>
              Sua primeira cobrança será em{' '}
              <strong>{formatDate(deferred.firstChargeAt, 'DD/MM/YYYY')}</strong>.
              <br />
              Até lá, seu acesso continua normalmente.
            </p>
            <Button
              onClick={onClose}
              fluid
              style={{ ...confirmButton, marginTop: 40 }}
            >
              Fechar
            </Button>
          </div>
        ) : screen === 'list' ? (
          <>
            <div className={cardList}>
              {creditCardTokens.map((card) => (
                <SavedCardItem
                  key={card.id}
                  card={card}
                  isSelected={card.id === effectiveSelectedId}
                  onSelect={setSelectedCardId}
                />
              ))}

              <button type="button" className={addMethodLink} onClick={handleShowNewCardForm}>
                + Adicionar novo método
              </button>
            </div>

            <Button
              onClick={handleConfirmSelection}
              loading={loading}
              disabled={loading || !effectiveSelectedId}
              fluid
              style={confirmButton}
            >
              Confirmar renovação
            </Button>
          </>
        ) : (
          <ChangeCardFormContainer
            onSubmit={handleNewCardSubmit}
            loading={loading}
            resetRef={formResetRef}
            showSetAsDefault={showSetAsDefault}
          />
        )}
      </Modal.Content>
    </Modal>
  );
}
